import { Trans, useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import { Checkbox } from 'designSystem/components/Checkbox'
import { CleaningControls } from 'features/timeSeries/experiment/CleaningControls'
import { CollapsibleSection } from 'features/timeSeries/experiment/CollapsibleSection'
import { CorrelationCard } from 'features/timeSeries/experiment/CorrelationCard'
import { DiagnosticsPanel } from 'features/timeSeries/experiment/DiagnosticsPanel'
import { useExperimentLab } from 'features/timeSeries/experiment/ExperimentLabContext'
import { KNOB_HELP } from 'features/timeSeries/experiment/knobCopy'
import { KnobHelp } from 'features/timeSeries/experiment/KnobHelp'
import { ModelKnobs } from 'features/timeSeries/experiment/ModelKnobs'
import { NormalizationViewer } from 'features/timeSeries/experiment/NormalizationViewer'
import { ResidualMiniPlot } from 'features/timeSeries/experiment/ResidualMiniPlot'
import { RunComparison } from 'features/timeSeries/experiment/RunComparison'
import {
  prepareRunsForSave,
  restoreExperimentRuns,
} from 'features/timeSeries/experiment/runPersistence'
import { SamplingKnobs } from 'features/timeSeries/experiment/SamplingKnobs'
import { SizedPlot } from 'features/timeSeries/experiment/SizedPlot'
import { SplitBuilder } from 'features/timeSeries/experiment/SplitBuilder'
import { TagLabel } from 'features/timeSeries/experiment/TagLabel'
import { TagPicker } from 'features/timeSeries/experiment/TagPicker'
import { TransparencyStrip } from 'features/timeSeries/experiment/TransparencyStrip'
import {
  ExperimentMethodKey,
  formatSavedExperimentLabel,
  mergeLoadedConfig,
} from 'features/timeSeries/experiment/types'
import { useLabBucketsOnChart } from 'features/timeSeries/experiment/useLabBucketsOnChart'
import { useLabTransformsOnChart } from 'features/timeSeries/experiment/useLabTransformsOnChart'
import { useScatterplotContext } from 'features/timeSeries/ScatterplotContext'
import { PlotData } from 'plotly.js'
import { FC, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'
import { mobileInputTextStyles } from 'styles/mobileInputTextStyles'

const FG = CantileverTheme.colors.grayscale['100']
const GRID = CantileverTheme.colors.grayscale['600']

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 0.25rem 0.5rem 0.75rem;
  font-size: 0.8rem;
`

const Hint = styled.p`
  margin: 0;
  font-size: 0.72rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const Group = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`

const GroupTitle = styled.div`
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  font-size: 0.72rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const Input = styled.input`
  padding: 0.3rem 0.45rem;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.forecast.pageBackground1};
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.78rem;
  ${mobileInputTextStyles}
`

const Select = styled.select`
  padding: 0.3rem 0.45rem;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.forecast.pageBackground1};
  color: ${({ theme }) => theme.forecast.defaultText};
  font-size: 0.78rem;
  ${mobileInputTextStyles}
`

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  align-items: center;
`

const Tabs = styled.div`
  display: flex;
  gap: 0.35rem;
  flex-wrap: wrap;
`

const TabBtn = styled.button<{ $active: boolean }>`
  border: 1px solid
    ${({ theme, $active }) =>
      $active ? theme.colors.blue['400'] : theme.forecast.border};
  border-radius: 0.375rem;
  padding: 0.35rem 0.85rem;
  background: ${({ theme, $active }) =>
    $active ? theme.forecast.background2 : theme.forecast.pageBackground1};
  color: ${({ theme }) => theme.forecast.defaultText};
  cursor: pointer;
  font-size: 0.82rem;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
`

const OffenderList = styled.ul`
  margin: 0.25rem 0 0;
  padding-left: 1.1rem;
  font-size: 0.75rem;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.forecast.secondaryText};

  li {
    margin-bottom: 0.15rem;
  }
`

const CheckRow = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.75rem;
  cursor: pointer;
`

const Error = styled.div`
  color: #ef4444;
  font-size: 0.78rem;
`

const ProgressLog = styled.div`
  max-height: 10rem;
  overflow-y: auto;
  font-size: 0.7rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 6px;
  padding: 0.4rem 0.5rem;
  background: ${({ theme }) => theme.forecast.pageBackground1};
  color: ${({ theme }) => theme.forecast.secondaryText};
  white-space: pre-wrap;
`

const TagStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const YRangeTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.72rem;

  th,
  td {
    text-align: left;
    padding: 0.25rem 0.35rem;
    border-bottom: 1px solid ${({ theme }) => theme.forecast.border};
    vertical-align: middle;
  }

  th {
    color: ${({ theme }) => theme.forecast.secondaryText};
    font-weight: 500;
  }

  input {
    width: 5.5rem;
  }
`

const short = (id: string) => id.split('-').slice(1).join('-') || id

type LabTab = 'data' | 'model'

/**
 * The Experiment Lab, rehosted as a Process Data sidebar tab. Time-series
 * surfaces (cleaned pens, removed points, expected/residual band) render on
 * the main viewer chart via pens + uPlot overlays; this tab keeps the config
 * knobs and the non-time-indexed diagnostics (loss curve, correlation vs
 * shift, residual histogram, kNN scatter) as Plotly cards. All session state
 * lives in ExperimentLabContext so tab switches don't lose a trained run.
 */
export const ExperimentTab: FC = () => {
  const { t } = useLingui()

  const {
    siteID,
    timeRange,
    resampleConfigs,
    setResampleConfigs,
    thresholdFilters,
    setThresholdFilters,
    smoothingConfigs,
    setSmoothingConfigs,
  } = useScatterplotContext()
  const {
    config,
    setConfig,
    patchConfig,
    api,
    runs,
    setRuns,
    selectedMethodKey,
    setSelectedMethodKey,
    showOverlays,
    setShowOverlays,
    targetOnlyView,
    setTargetOnlyView,
    outOfBandPoints,
    saveName,
    setSaveName,
    savedEntryId,
    setSavedEntryId,
    lagsText,
    setLagsText,
    hiddenText,
    setHiddenText,
    useViewerRange,
    resetViewerToExperimentWindow,
    markEngaged,
  } = useExperimentLab()

  const [tab, setTab] = useState<LabTab>('data')
  const [selectedListId, setSelectedListId] = useState('')
  const [includeModels, setIncludeModels] = useState(false)
  const [saveMessage, setSaveMessage] = useState<string | null>(null)

  const labBuckets = useLabBucketsOnChart(
    config,
    resampleConfigs,
    setResampleConfigs
  )

  const { cleaningFilters, denoise, rollingFeature } = useLabTransformsOnChart(
    config,
    thresholdFilters,
    setThresholdFilters,
    smoothingConfigs,
    setSmoothingConfigs
  )

  // Opening the tab engages the session: the provider's preview fetch and
  // date-window push stay dormant until a user has actually been here.
  useEffect(() => {
    markEngaged()
  }, [markEngaged])

  const { listSaved } = api
  useEffect(() => {
    void listSaved()
  }, [listSaved, siteID])

  // Keep the saved-list select pointed at a valid entry (prefer editing id).
  useEffect(() => {
    setSelectedListId((prev) => {
      if (!api.savedList.length) return ''
      if (prev && api.savedList.some((x) => x.entry_id === prev)) return prev
      if (
        savedEntryId &&
        api.savedList.some((x) => x.entry_id === savedEntryId)
      ) {
        return savedEntryId
      }
      return api.savedList[0].entry_id
    })
  }, [api.savedList, savedEntryId])

  const splitChunks = api.preview?.split_chunks ?? []

  const studioTags = useMemo(
    () => [config.target, ...config.drivers].filter(Boolean),
    [config.target, config.drivers]
  )

  const onTrain = async () => {
    setTab('model')
    const result = await api.train(config)
    if (!result?.ok) return
    setRuns((prev) => [
      {
        id: `${Date.now()}`,
        createdAt: new Date().toISOString(),
        label: t`Run ${{ value301_24: prev.length + 1 }}`,
        config: structuredClone(config),
        result,
      },
      ...prev,
    ])
  }

  const onSave = async (asNew = false) => {
    const name =
      saveName.trim() ||
      (config.target ? short(config.target) : '') ||
      'experiment'
    const persistedRuns = prepareRunsForSave(runs, config, api.trainResult)
    setRuns(persistedRuns)
    setSaveMessage(null)
    const res = await api.saveExperiment({
      name,
      config,
      result: api.trainResult,
      runs: persistedRuns,
      notes: config.notes || '',
      entryId: asNew ? null : savedEntryId,
      includeModels,
    })
    if (!res) return
    setSavedEntryId(res.entry_id)
    setSaveName(res.name)
    setSaveMessage(
      res.warnings?.length
        ? t`Saved (${{ value331_22: res.warnings.join('; ') }})`
        : t`Saved as ${{ value332_24: res.name }}`
    )
  }

  const onLoadSaved = async (entryId: string) => {
    const loaded = await api.loadExperiment(entryId)
    if (!loaded?.config) return
    const next = mergeLoadedConfig(loaded.config)
    const restoredRuns = restoreExperimentRuns(loaded, next)
    setConfig(next)
    setRuns(restoredRuns)
    setLagsText((next.feature.driver_lags || []).join(','))
    setHiddenText((next.mlp_hidden_layer_sizes || []).join(','))
    setSavedEntryId(loaded.entry_id)
    setSaveName(loaded.name || '')
    const restoredResult =
      loaded.result?.ok && loaded.result
        ? loaded.result
        : restoredRuns.find((run) => run.result.ok)?.result || null
    if (restoredResult) {
      api.setTrainResult(restoredResult)
      setTab('model')
    } else {
      api.setTrainResult(null)
      setTab('data')
    }
    const warnings = loaded.warnings?.join('; ')
    setSaveMessage(
      t`Loaded ${{ value360_18: loaded.name || entryId }}${{ value360_43: warnings ? ` (${warnings})` : '' }}`
    )
  }

  const onDeleteSelected = async () => {
    if (!selectedListId) return
    const item = api.savedList.find((x) => x.entry_id === selectedListId)
    const label = item?.name || selectedListId
    if (!window.confirm(t`Delete saved experiment "${label}"?`)) return
    const ok = await api.deleteExperiment(selectedListId)
    if (!ok) return
    if (savedEntryId === selectedListId) {
      setSavedEntryId(null)
      setSaveMessage(t`Deleted current save`)
    } else {
      setSaveMessage(t`Deleted ${label}`)
    }
  }

  const setYBound = (tag: string, bound: 'min' | 'max', raw: string) => {
    const value = raw.trim() === '' ? undefined : Number(raw)
    if (value !== undefined && !Number.isFinite(value)) return
    const prev = config.y_ranges || {}
    const entry = { ...prev[tag], [bound]: value }
    const next = { ...prev }
    if (entry.min === undefined && entry.max === undefined) {
      delete next[tag]
    } else {
      next[tag] = entry
    }
    patchConfig({ y_ranges: next })
  }

  const toggleMethod = (key: ExperimentMethodKey) => {
    const has = config.methods.includes(key)
    patchConfig({
      methods: has
        ? config.methods.filter((m) => m !== key)
        : [...config.methods, key],
    })
  }

  return (
    <Wrap>
      <Hint>
        <Trans>
          The chart&apos;s pens and the experiment&apos;s tags are the same set:
          every pen on the chart (including ones already there) joins the
          experiment — the first as target, the rest as drivers — and
          target/driver tags picked here plot as pens. Removing either side
          removes both. Removed points and the trained expected/residual band
          draw over the pens, with the chart&apos;s zoom, tooltip, and ruler.
          Diagnostics live here.
        </Trans>
      </Hint>

      <Row>
        <CheckRow>
          <Checkbox
            checked={showOverlays}
            onCheckedChange={(v) => setShowOverlays(!!v)}
          />
          <Trans>Draw experiment overlays on the chart</Trans>
        </CheckRow>
        <CheckRow>
          <Checkbox
            checked={targetOnlyView}
            onCheckedChange={(v) => setTargetOnlyView(!!v)}
          />
          <Trans>Target only (hide driver pens)</Trans>
        </CheckRow>
      </Row>

      <CollapsibleSection
        sectionKey='savedExperiments'
        title={t`Saved experiments`}
        defaultOpen={false}>
        <Field>
          <Trans>Name</Trans>
          <Input
            value={saveName}
            placeholder={
              config.target ? short(config.target) : t`experiment name`
            }
            onChange={(e) => setSaveName(e.target.value)}
          />
        </Field>
        <CheckRow>
          <Checkbox
            checked={includeModels}
            onCheckedChange={(v) => setIncludeModels(!!v)}
          />
          <Trans>Also save model weights (slower re-fit)</Trans>
        </CheckRow>
        <Row>
          <Button
            disabled={
              !config.target || !config.drivers.length || api.saveLoading
            }
            onClick={() => void onSave(false)}>
            {api.saveLoading
              ? t`Saving…`
              : savedEntryId
                ? t`Update save`
                : t`Save`}
          </Button>
          {savedEntryId && (
            <Button
              variant='outline'
              disabled={api.saveLoading}
              onClick={() => void onSave(true)}>
              <Trans>Save as new</Trans>
            </Button>
          )}
        </Row>
        {api.saveError && <Error>{api.saveError}</Error>}
        {saveMessage && <Hint>{saveMessage}</Hint>}
        {savedEntryId && (
          <Hint>
            <Trans>Editing: </Trans>
            {saveName || 'untitled'} ({savedEntryId})
          </Hint>
        )}
        {api.savedError && <Error>{api.savedError}</Error>}
        {api.savedList.length === 0 && !api.savedLoading ? (
          <Hint>
            <Trans>No saved experiments</Trans>
          </Hint>
        ) : (
          <>
            <Field>
              <Trans>Saved</Trans>
              <Select
                value={selectedListId}
                disabled={!api.savedList.length || api.savedLoading}
                onChange={(e) => setSelectedListId(e.target.value)}>
                {api.savedList.map((item) => (
                  <option key={item.entry_id} value={item.entry_id}>
                    {formatSavedExperimentLabel(item)}
                  </option>
                ))}
              </Select>
            </Field>
            <Row>
              <Button
                variant='outline'
                disabled={!selectedListId || api.loadLoading}
                onClick={() => void onLoadSaved(selectedListId)}>
                {api.loadLoading ? t`Loading…` : t`Load`}
              </Button>
              <Button
                variant='outline'
                disabled={!selectedListId}
                onClick={() => void onDeleteSelected()}>
                <Trans>Delete</Trans>
              </Button>
              <Button
                variant='outline'
                disabled={api.savedLoading}
                onClick={() => void listSaved()}>
                {api.savedLoading ? t`Refreshing…` : t`Refresh`}
              </Button>
            </Row>
          </>
        )}
      </CollapsibleSection>

      <CollapsibleSection sectionKey='tags' title={t`Target / Drivers`}>
        <TagPicker
          siteId={siteID}
          label={<KnobHelp label={t`Target`} help={KNOB_HELP.target} />}
          value={config.target ? [config.target] : []}
          onChange={(ids) => patchConfig({ target: ids[0] || '' })}
          exclude={config.drivers}
        />
        <TagPicker
          siteId={siteID}
          label={<KnobHelp label={t`Drivers`} help={KNOB_HELP.drivers} />}
          value={config.drivers}
          onChange={(ids) => patchConfig({ drivers: ids })}
          multi
          exclude={config.target ? [config.target] : []}
        />
      </CollapsibleSection>

      <CollapsibleSection
        sectionKey='yRanges'
        title={t`Y axis ranges`}
        defaultOpen={false}>
        <Hint>
          <Trans>
            Pin a tag&apos;s chart Y range (custom bounds win over auto).
            Applies to the viewer&apos;s pens immediately and saves with the
            experiment. Leave a field empty for auto.
          </Trans>
        </Hint>
        {studioTags.length === 0 ? (
          <Hint>
            <Trans>Pick a target and drivers above.</Trans>
          </Hint>
        ) : (
          <YRangeTable>
            <thead>
              <tr>
                <th>
                  <Trans>Tag</Trans>
                </th>
                <th>
                  <Trans>Y min</Trans>
                </th>
                <th>
                  <Trans>Y max</Trans>
                </th>
              </tr>
            </thead>
            <tbody>
              {studioTags.map((tag) => (
                <tr key={tag}>
                  <td>
                    <TagLabel tagId={tag}>
                      <span>
                        {short(tag)}
                        {tag === config.target ? t` · target` : ''}
                      </span>
                    </TagLabel>
                  </td>
                  <td>
                    <Input
                      type='number'
                      step='any'
                      placeholder='auto'
                      value={config.y_ranges?.[tag]?.min ?? ''}
                      onChange={(e) => setYBound(tag, 'min', e.target.value)}
                    />
                  </td>
                  <td>
                    <Input
                      type='number'
                      step='any'
                      placeholder='auto'
                      value={config.y_ranges?.[tag]?.max ?? ''}
                      onChange={(e) => setYBound(tag, 'max', e.target.value)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </YRangeTable>
        )}
      </CollapsibleSection>

      <CollapsibleSection sectionKey='dateWindow' title={t`Date window`}>
        <Row>
          <Field>
            <KnobHelp label={t`Start`} help={KNOB_HELP.startDate} />
            <Input
              type='date'
              value={config.start_date}
              onChange={(e) => patchConfig({ start_date: e.target.value })}
            />
          </Field>
          <Field>
            <KnobHelp label={t`End`} help={KNOB_HELP.endDate} />
            <Input
              type='date'
              value={config.end_date}
              onChange={(e) => patchConfig({ end_date: e.target.value })}
            />
          </Field>
        </Row>
        <Row>
          <Button variant='outline' onClick={useViewerRange}>
            <Trans>Use chart range</Trans>
          </Button>
          <Button
            variant='outline'
            disabled={!config.start_date || !config.end_date}
            onClick={resetViewerToExperimentWindow}>
            <Trans>Reset chart to window</Trans>
          </Button>
          <Hint>
            <Trans>Chart shows </Trans>
            {timeRange.from.slice(0, 10)} → {timeRange.to.slice(0, 10)}
            <Trans>. Changing the dates above moves the chart to match.</Trans>
          </Hint>
        </Row>
        <SamplingKnobs
          config={config}
          onPatch={patchConfig}
          chartBuckets={labBuckets}
        />
      </CollapsibleSection>

      <Tabs>
        {(
          [
            ['data', t`Data Studio`],
            ['model', t`Model Studio`],
          ] as const
        ).map(([id, label]) => (
          <TabBtn
            key={id}
            type='button'
            $active={tab === id}
            onClick={() => setTab(id)}>
            {label}
          </TabBtn>
        ))}
      </Tabs>

      {tab === 'data' && (
        <>
          <TransparencyStrip
            funnel={api.preview?.funnel ?? null}
            alignment={
              api.preview?.alignment ?? api.preview?.funnel?.alignment ?? null
            }
            tagStats={api.preview?.tag_stats}
            loading={api.previewLoading}
            error={api.previewError}
          />

          {api.previewError && <Error>{api.previewError}</Error>}
          {api.previewLoading && !api.preview && (
            <Hint>
              <Trans>Loading data preview…</Trans>
            </Hint>
          )}

          <CollapsibleSection sectionKey='cleaning' title={t`Cleaning`}>
            <Row>
              <Button
                variant='outline'
                onClick={() =>
                  patchConfig({ cleaning_plan: { tags: {}, summary: '' } })
                }>
                <Trans>Clear ops</Trans>
              </Button>
              {api.previewLoading && api.preview && (
                <Hint>
                  <Trans>Refreshing preview…</Trans>
                </Hint>
              )}
            </Row>
            <CheckRow>
              <Checkbox
                checked={config.gate_scope !== 'train_only'}
                onCheckedChange={(v) =>
                  patchConfig({ gate_scope: v ? 'all' : 'train_only' })
                }
              />
              <KnobHelp
                label={t`Apply gate to test range`}
                help={KNOB_HELP.gateScope}
              />
            </CheckRow>
            {config.gate_scope === 'train_only' && (
              <Hint>
                <Trans>
                  Gates only remove train/val rows — the test range keeps every
                  row, so held-out metrics stay honest about all operating
                  regimes.
                </Trans>
              </Hint>
            )}
            <CheckRow>
              <Checkbox
                checked={cleaningFilters.active}
                disabled={cleaningFilters.unavailableReason !== null}
                onCheckedChange={(v) => cleaningFilters.setActive(v === true)}
              />
              <Trans>Show range / gate as pen filters</Trans>
            </CheckRow>
            {cleaningFilters.unavailableReason !== null ? (
              <Hint>{cleaningFilters.unavailableReason}</Hint>
            ) : (
              cleaningFilters.active && (
                <Hint>
                  <Trans>
                    Each bounded tag marks its own excluded points. A gate drops
                    those rows for every tag in training, but on the chart it
                    marks only its own pen. Quantile clip and frozen have no
                    chart equivalent.
                  </Trans>
                </Hint>
              )
            )}
            <CheckRow>
              <Checkbox
                checked={denoise.active}
                disabled={denoise.unavailableReason !== null}
                onCheckedChange={(v) => denoise.setActive(v === true)}
              />
              <Trans>Show denoise as a rolling overlay</Trans>
            </CheckRow>
            {denoise.unavailableReason !== null ? (
              <Hint>{denoise.unavailableReason}</Hint>
            ) : (
              denoise.active && (
                <Hint>
                  <Trans>Centered window over each denoised tag, </Trans>
                  {config.interval} {config.interval_unit}
                  <Trans> per period, drawn beside the raw pen.</Trans>
                </Hint>
              )
            )}
            <Hint>
              <Trans>Plan tags: </Trans>
              {Object.keys(config.cleaning_plan.tags || {}).length} ·{' '}
              {config.cleaning_plan.summary || t`no summary`}
            </Hint>

            <TagStack>
              {studioTags.length === 0 && (
                <Hint>
                  <Trans>Pick a target and drivers above.</Trans>
                </Hint>
              )}
              {studioTags.map((tag) => (
                <CleaningControls
                  key={tag}
                  tag={tag}
                  isTarget={tag === config.target}
                  removed={api.preview?.removed_points?.[tag] ?? null}
                  tagStats={api.preview?.tag_stats?.[tag]}
                  rawStats={api.preview?.tag_stats_raw?.[tag]}
                  cleaningPlan={config.cleaning_plan}
                  onCleaningPlanChange={(cleaning_plan) =>
                    patchConfig({ cleaning_plan })
                  }
                />
              ))}
            </TagStack>
          </CollapsibleSection>

          <CollapsibleSection sectionKey='correlation' title={t`Correlation`}>
            <CorrelationCard
              correlation={api.preview?.cross_correlation}
              drivers={config.drivers}
              phaseShifts={config.phase_shifts}
              corrShiftMax={config.corr_shift_max}
              corrShiftStep={config.corr_shift_step}
              interval={config.interval}
              intervalUnit={config.interval_unit}
              onCorrShiftMaxChange={(n) => patchConfig({ corr_shift_max: n })}
              onCorrShiftStepChange={(n) => patchConfig({ corr_shift_step: n })}
              onPhaseShiftsChange={(phase_shifts) =>
                patchConfig({ phase_shifts })
              }
            />
          </CollapsibleSection>

          <CollapsibleSection
            sectionKey='normalization'
            title={t`Normalization`}>
            <NormalizationViewer
              siteId={siteID}
              config={config}
              result={api.normalizeResult}
              loading={api.normalizeLoading}
              error={api.normalizeError}
              onRun={(against, method) => {
                void api.normalize(config, against, method)
              }}
            />
          </CollapsibleSection>

          <CollapsibleSection
            sectionKey='cleaningJson'
            title={t`Advanced: cleaning plan JSON`}
            defaultOpen={false}>
            <Field>
              <KnobHelp
                label={t`Edit plan JSON`}
                help={KNOB_HELP.cleaningJson}
              />
              <textarea
                style={{
                  width: '100%',
                  minHeight: '7rem',
                  fontSize: '0.7rem',
                  fontFamily: 'monospace',
                }}
                value={JSON.stringify(config.cleaning_plan, null, 2)}
                onChange={(e) => {
                  try {
                    patchConfig({ cleaning_plan: JSON.parse(e.target.value) })
                  } catch {
                    /* ignore while typing */
                  }
                }}
              />
            </Field>
          </CollapsibleSection>
        </>
      )}

      {tab === 'model' && (
        <>
          <CollapsibleSection
            sectionKey='split'
            title={
              <KnobHelp
                label={t`Split builder`}
                help={KNOB_HELP.splitBuilder}
              />
            }>
            <SplitBuilder
              split={config.split}
              chunks={splitChunks}
              windowStart={config.start_date}
              windowEnd={config.end_date}
              onChange={(split) => patchConfig({ split })}
            />
          </CollapsibleSection>

          <CollapsibleSection sectionKey='knobs' title={t`Model knobs`}>
            <ModelKnobs
              config={config}
              onPatch={patchConfig}
              onFeaturePatch={(patch) =>
                patchConfig({ feature: { ...config.feature, ...patch } })
              }
              onToggleMethod={toggleMethod}
              lagsText={lagsText}
              onLagsTextChange={setLagsText}
              hiddenText={hiddenText}
              onHiddenTextChange={setHiddenText}
              chartRollingFeature={rollingFeature}
            />
          </CollapsibleSection>

          <Row>
            <Button
              disabled={
                !config.target ||
                !config.drivers.length ||
                !config.methods.length ||
                api.trainLoading
              }
              onClick={() => void onTrain()}>
              {api.trainLoading
                ? t`Training…`
                : t({ message: 'Train', context: 'model training action' })}
            </Button>
            {api.trainError && <Error>{api.trainError}</Error>}
          </Row>

          {(api.trainLoading || api.trainProgress.length > 0) && (
            <Group>
              <GroupTitle>
                <Trans>Live train progress</Trans>
              </GroupTitle>
              <ProgressLog>
                {api.trainProgress.length
                  ? api.trainProgress
                      .map((e) => e.message || e.type || e.stage || '')
                      .filter(Boolean)
                      .join('\n')
                  : t`Starting…`}
              </ProgressLog>
              {Object.keys(api.liveLossCurves).length > 0 && (
                <SizedPlot
                  $heightRem={12}
                  data={Object.entries(api.liveLossCurves).flatMap(
                    ([method, curve], i) => {
                      const color = [
                        '#22d3ee',
                        '#f97316',
                        '#a78bfa',
                        '#34d399',
                      ][i % 4]
                      const traces: Partial<PlotData>[] = [
                        {
                          x: curve.train_loss.map((_, j) => j + 1),
                          y: curve.train_loss,
                          type: 'scatter',
                          mode: 'lines',
                          name: `${method} train`,
                          line: { width: 1.3, color },
                        },
                      ]
                      if (curve.val_loss.length) {
                        traces.push({
                          x: curve.val_loss.map((_, j) => j + 1),
                          y: curve.val_loss,
                          type: 'scatter',
                          mode: 'lines',
                          name: `${method} val`,
                          line: { width: 1.1, color, dash: 'dot' },
                        })
                      }
                      return traces
                    }
                  )}
                  layout={{
                    paper_bgcolor: 'rgba(0,0,0,0)',
                    plot_bgcolor: 'rgba(0,0,0,0)',
                    margin: { l: 48, r: 12, t: 28, b: 36 },
                    legend: { font: { color: FG, size: 10 } },
                    title: {
                      text: 'Live MLP loss (streaming)',
                      font: { color: FG, size: 12 },
                      x: 0,
                    },
                    xaxis: {
                      title: {
                        text: 'epoch',
                        font: { color: FG, size: 10 },
                      },
                      tickfont: { color: FG, size: 10 },
                      gridcolor: GRID,
                    },
                    yaxis: {
                      tickfont: { color: FG, size: 10 },
                      gridcolor: GRID,
                    },
                  }}
                />
              )}
            </Group>
          )}

          <CollapsibleSection sectionKey='diagnostics' title={t`Diagnostics`}>
            {api.trainResult && (
              <Group>
                <GroupTitle>
                  <Trans>Test residual</Trans>
                </GroupTitle>
                <ResidualMiniPlot
                  result={api.trainResult}
                  methodKey={selectedMethodKey}
                />
              </Group>
            )}

            {api.trainResult && showOverlays && (
              <Group>
                <GroupTitle>
                  <Trans>Out-of-band points</Trans>
                </GroupTitle>
                {outOfBandPoints.length === 0 ? (
                  <Hint>
                    <Trans>
                      No points left the expected ± band for the selected
                      method.
                    </Trans>
                  </Hint>
                ) : (
                  <>
                    <Hint>
                      {outOfBandPoints.length} point
                      {outOfBandPoints.length === 1 ? '' : 's'}
                      <Trans>
                        {' '}
                        outside the band (white dots on the chart). Worst
                        offenders:
                      </Trans>
                    </Hint>
                    <OffenderList>
                      {[...outOfBandPoints]
                        .sort((a, b) => b.severity - a.severity)
                        .slice(0, 5)
                        .map((point) => (
                          <li key={point.x}>
                            {new Date(point.x).toISOString().slice(0, 16)}
                            <Trans>Z — actual </Trans>
                            {point.actual.toFixed(3)}
                            <Trans> vs expected</Trans>{' '}
                            {point.expected.toFixed(3)} (
                            {Number.isFinite(point.severity)
                              ? t`${{ value1029_37: point.severity.toFixed(1) }}× band`
                              : t`zero-width band`}
                            )
                          </li>
                        ))}
                    </OffenderList>
                  </>
                )}
              </Group>
            )}

            {api.trainResult ? (
              <DiagnosticsPanel
                result={api.trainResult}
                methodKey={selectedMethodKey}
                onMethodKeyChange={setSelectedMethodKey}
                overlayNote={t`Actual vs expected and the residual band for this method draw directly on the main chart (target pen + shaded band), where zoom, tooltip, and ruler work.`}
              />
            ) : (
              !api.trainLoading && (
                <Hint>
                  <Trans>
                    Hit Train to band the target pen on the chart and see
                    metrics here.
                  </Trans>
                </Hint>
              )
            )}
          </CollapsibleSection>

          <CollapsibleSection sectionKey='compareRuns' title={t`Compare runs`}>
            <RunComparison
              runs={runs}
              onReload={(c) => {
                setConfig(c)
                setLagsText(c.feature.driver_lags.join(','))
                setHiddenText(c.mlp_hidden_layer_sizes.join(','))
                setTab('data')
              }}
              onClear={() => setRuns([])}
            />
          </CollapsibleSection>
        </>
      )}
    </Wrap>
  )
}
